import { type PropsWithChildren, useState } from "react";
import { NavLink } from "react-router-dom";
import type { Request } from "../../common/types/Request";
import "./MemberRequests.css";
import { useGet } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import { Lib, type TimelineItemDateProps } from "albertine-shared-web";
import type { Booking } from "../../common/types/Booking";
import {
    CategoriesWithUncategorized,
    type FilterByCategory,
} from "../../common/types/OfferingCategory";
import {
    requestCategoryMatchesOnAny,
    requestsByCategories,
} from "../../common/utils/requests.util";
import { singularPlural } from "../../common/utils/time.formatted.util";
import { mapApiBookingToBooking } from "../../utils/booking.adapter";
import { sortRequests } from "../../utils/sort.util";
import { combineDateAndTimeString } from "../../utils/time.util";
import { Flex } from "../Common/Flex";
import { PictogramIcon } from "../Common/Icon";
import Label from "../Common/Label";
import ListFilter from "../Common/ListFilter";
import Paragraph from "../Common/Paragraph";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import RequestSnippet from "../Request/RequestSnippet";

type Props = {
    member: MemberDataFirestoreRecord;
    requests: Request[] | undefined;
};

function getRequestUrl(requestId: string) {
    return `/request/${requestId}`;
}

function RequestSnippetTitle(props: { text: string; count: number }) {
    const { count, text } = props;
    return (
        <Flex.Row
            justifyContent="space-between"
            className="member-requests__snippet-title"
        >
            <h4>{text}</h4>
            <Label.Small.Normal.N50>{count}</Label.Small.Normal.N50>
        </Flex.Row>
    );
}

function NewRequests(props: { requests: Request[] }) {
    const { requests } = props;

    return requests.length !== 0 ? (
        <Flex.Column gap="medium-plus" className="member-requests__new-requests">
            <RequestSnippetTitle text="New" count={requests.length} />
            {requests.map((request) => (
                <NavLink
                    className="member-requests__request-link"
                    key={request.id}
                    id={request.id}
                    to={getRequestUrl(request.id)}
                >
                    <RequestSnippet request={request} confirmedBookings={[]} />
                </NavLink>
            ))}
        </Flex.Column>
    ) : null;
}

function DraftRequests(props: {
    requests: Request[];
    groupedBookings: { request: string; bookings: Booking[] }[];
}) {
    const { requests, groupedBookings } = props;

    return requests.length !== 0 ? (
        <Flex.Column gap="medium-plus" className="member-requests__draft-requests">
            <RequestSnippetTitle text="Drafts" count={requests.length} />
            {requests.map((request) => (
                <NavLink
                    className="member-requests__request-link"
                    key={request.id}
                    id={request.id}
                    to={getRequestUrl(request.id)}
                >
                    <RequestSnippet
                        request={request}
                        confirmedBookings={groupedBookings}
                    />
                </NavLink>
            ))}
        </Flex.Column>
    ) : null;
}

function ActiveRequests(props: {
    requests: Request[];
    groupedBookings: { request: string; bookings: Booking[] }[];
}) {
    const { requests, groupedBookings } = props;

    return requests.length !== 0 ? (
        <Flex.Column gap="xlarge" className="member-requests__active-requests">
            <RequestSnippetTitle text="Active" count={requests.length} />

            <Lib.Timeline<Request & TimelineItemDateProps<Request>>
                monthGap="value12"
                wrapperGap="value32"
                itemGap="value16"
                order="newest-first"
                labelComponent={TimelineLabel}
                items={requests.map((it) => ({
                    ...it,
                    getDate: (r) => {
                        const combined = combineDateAndTimeString(
                            r?.startDate ?? null,
                            r?.startTime ?? null,
                        );
                        const date = combined ?? r?.startDate ?? undefined;
                        return date ? new Date(date) : undefined;
                    },
                }))}
                renderItem={(request) => (
                    <NavLink
                        className="member-requests__request-link"
                        key={request.id}
                        id={request.id}
                        to={getRequestUrl(request.id)}
                    >
                        <RequestSnippet
                            key={request.id}
                            request={request}
                            confirmedBookings={groupedBookings}
                        />
                    </NavLink>
                )}
            />
        </Flex.Column>
    ) : null;
}

function ClosedRequests(props: {
    requests: Request[];
    groupedBookings: { request: string; bookings: Booking[] }[];
}) {
    const { requests, groupedBookings } = props;
    const sortedRequests = sortRequests(requests, {
        by: "eventDate",
        order: "desc",
    });

    return sortedRequests.length !== 0 ? (
        <Flex.Column gap="xlarge" className="member-requests__closed-requests">
            <RequestSnippetTitle text="Closed" count={sortedRequests.length} />
            <Lib.Timeline<Request & TimelineItemDateProps<Request>>
                monthGap="value12"
                itemGap="value16"
                wrapperGap="value32"
                order="oldest-first"
                labelComponent={TimelineLabel}
                items={requests.map((it) => ({
                    ...it,
                    getDate: (r) => {
                        const combined = combineDateAndTimeString(
                            r?.startDate ?? null,
                            r?.startTime ?? null,
                        );
                        const date = combined ?? r?.startDate ?? undefined;
                        return date ? new Date(date) : undefined;
                    },
                }))}
                renderItem={(request) => (
                    <NavLink
                        className="member-requests__request-link"
                        key={request.id}
                        id={request.id}
                        to={getRequestUrl(request.id)}
                    >
                        <RequestSnippet
                            key={request.id}
                            request={request}
                            confirmedBookings={groupedBookings}
                        />
                    </NavLink>
                )}
            />
        </Flex.Column>
    ) : null;
}

function RequestList(props: {
    requests: Request[] | undefined;
    member: MemberDataFirestoreRecord;
}) {
    const { requests, member } = props;
    const [filterSettings, setFilterSettings] = useState<{
        selectedCategories: CategoriesWithUncategorized[];
    }>({
        selectedCategories: [],
    });

    const { data: confirmedBookingsData } = useGet("bookings", {
        query: { memberId: member.id, reservationStatus: "confirmed" },
    });

    const confirmedBookings: Booking[] | undefined =
        confirmedBookingsData?.bookings.map(mapApiBookingToBooking);

    const categories = requestsByCategories(requests ?? []);
    const sortedRequests = sortRequests(requests ?? [], {
        by: "eventDate",
        order: "asc",
    });
    const groupedConfirmedBookings = (requests ?? []).map((request) => ({
        request: request.id,
        bookings: (confirmedBookings ?? []).filter(
            (booking) => booking.request === request.id,
        ),
    }));

    const groups: Record<"new" | "draft" | "active" | "closed", Request[]> = {
        new: sortedRequests
            .filter((req) => req.status === "new")
            .filter(requestCategoryMatchesOnAny(filterSettings.selectedCategories)),
        draft: sortedRequests
            .filter((req) => req.status === "draft")
            .filter(requestCategoryMatchesOnAny(filterSettings.selectedCategories)),
        active: sortedRequests
            .filter(
                (req) =>
                    req.status !== "new" &&
                    req.status !== "closed" &&
                    req.status !== "draft",
            )
            .filter(requestCategoryMatchesOnAny(filterSettings.selectedCategories)),
        closed: sortedRequests
            .filter((req) => req.status === "closed")
            .filter(requestCategoryMatchesOnAny(filterSettings.selectedCategories)),
    };

    if (!requests) {
        return (
            <Flex.Column
                alignItems="center"
                justifyContent="center"
                className="member-requests__loading"
            >
                <LoadingSpinner />
            </Flex.Column>
        );
    }

    if (sortedRequests.length === 0) {
        return (
            <Flex.Column className="member-requests__empty-state">
                <Flex.Column
                    alignItems="center"
                    gap="large"
                    className="member-requests__empty-state-inner"
                >
                    <div className="member-requests__empty-state-icon">
                        <PictogramIcon.Concierge.Large.T90 />
                    </div>

                    <Flex.Column gap="medium" alignItems="center">
                        <h3>No requests yet</h3>
                        <Paragraph.Medium.Light.N50>
                            Create a new request from any message in the chat
                        </Paragraph.Medium.Light.N50>
                    </Flex.Column>
                </Flex.Column>
            </Flex.Column>
        );
    }

    return (
        <>
            <Flex.Row
                className="member-requests__header"
                justifyContent="space-between"
            >
                <h4>
                    {singularPlural(
                        `${sortedRequests.length} request`,
                        `${sortedRequests.length} requests`,
                        sortedRequests.length,
                    )}
                </h4>
                <ListFilter<FilterByCategory>
                    items={CategoriesWithUncategorized.options.map((categoryId) => ({
                        data: categories[categoryId],
                        selected:
                            filterSettings.selectedCategories.includes(categoryId),
                        count: categories[categoryId].items.length,
                    }))}
                    onChange={(newSelections) => {
                        const selectedIds = newSelections.map((it) => it.id);
                        setFilterSettings({
                            selectedCategories: selectedIds,
                        });
                    }}
                    position="bottom-right"
                />
            </Flex.Row>
            <Flex.Column>
                <NewRequests requests={groups.new} />

                <Flex.Column className="member-requests__section-wrapper">
                    <DraftRequests
                        requests={groups.draft}
                        groupedBookings={groupedConfirmedBookings}
                    />
                </Flex.Column>

                <ActiveRequests
                    requests={groups.active}
                    groupedBookings={groupedConfirmedBookings}
                />

                <Flex.Column className="member-requests__section-wrapper">
                    <ClosedRequests
                        requests={groups.closed}
                        groupedBookings={groupedConfirmedBookings}
                    />
                </Flex.Column>
            </Flex.Column>
        </>
    );
}

function MemberRequests(props: Props) {
    const { member, requests } = props;

    return (
        <Flex.Column className="member-requests">
            <RequestList requests={requests} member={member} />
        </Flex.Column>
    );
}

const TimelineLabel = (props: PropsWithChildren<{ className?: string }>) => {
    return (
        <Label.Small.SemiBold.N50 className={props.className}>
            {props.children}
        </Label.Small.SemiBold.N50>
    );
};

export default MemberRequests;
